import { mapState, mapGetters } from 'vuex';
import validate from 'mixins/validate';
import { decodeObjectID } from 'utils/general';
import Klaviyo from 'lib/klaviyo.js';
import { i18n } from 'lib/i18n.js';
import { trackGetNotified } from 'lib/tracking';

export default {
  i18n,
  mixins: [validate],
  components: {
    checkbox: () => import('../checkbox/checkbox.vue'),
    'text-input': () => import('../text-input/text-input.vue'),
  },
  data() {
    return {
      shouldSubscribe: false,
      finalMessage: '',
      formErrors: [],
      subscribed: false,
      subscribedVariantIds: [],
      loading: false
    };
  },
  mounted() {
    this.finalMessage = '';
    this.formErrors = [];
  },
  computed: {
    ...mapState('global', ['backInStock']),
    ...mapState('product', ['product', 'variant']),
    ...mapGetters('product', ['productTitle']),
    heading() {
      return this.subscribed
        ? this.$t('products.waitlist.success_title')
        : this.$t('products.waitlist.title');
    },
    subtitle() {
      return this.subscribed
        ? this.$t('products.waitlist.success_description')
        : this.$t('products.waitlist.description_html');
    },
  },
  watch: {
    product() {
      let variantId = this.variant?.id;

      // Check if it's a base64 style ID from storefront that needs decoding (as will be on PDP)
      const base64 = /[a-zA-Z]/g;
      if (base64.exec(variantId) !== null) {
        variantId = decodeObjectID(this.variant?.id, 'ProductVariant');
      }

      // Reset form if user changes variant
      this.subscribed = this.subscribedVariantIds.includes(variantId);
    },
  },
  methods: {
    onEmailChange({ error }) {
      this.valueChange();
      if (error) {
        this.$refs.notifyButton.classList.add(
          'product-notify-me-form__button--error',
        );
        this.$refs.notifyButton.disabled = true;
      } else {
        this.$refs.notifyButton.classList.remove(
          'product-notify-me-form__button--error',
        );
        this.$refs.notifyButton.disabled = false;
      }
    },
    handleTracking() {
      dataLayer.push({
        event: 'product_module_interaction',
        module_name: 'notify_me',
        click_action: 'submit',
        product_name: this.productTitle,
        shopify_module_name: 'product notify me form'
      });
    },
    onCheckboxChange({ value }) {
      this.shouldSubscribe = !!value;
    },
    async addToWaitList() {
      const formData = this.formData();
      if (!Object.hasOwn(formData, 'email')) {
        this.$refs.notifyEmail.setDirty();
        return;
      }

      const email = formData.email.toString();
      const productId = this.product?.id.toString();
      const productSKU =
        typeof this.product.sku !== 'undefined'
          ? this.product.sku
          : this.product.variants
            ? this.product.variants[0].sku
            : undefined;
      let variantId = this.variant?.id;

      // Check if it's a base64 style ID from storefront that needs decoding (as will be on PDP)
      const base64 = /[a-zA-Z]/g;
      if (base64.exec(variantId) !== null) {
        variantId = decodeObjectID(this.variant?.id, 'ProductVariant');
      }

      if (productId && variantId && email) {
        this.formErrors = [];
        try {
          this.loading = true;
          const requests = [
            Klaviyo.backInStockSubscribe({
              email,
              variant: variantId
            }),
          ];

          if (this.shouldSubscribe) {
            requests.push(
              Klaviyo.subscribe({
                email,
                list_id: __GLOBAL__.klaviyoListId,
                source: 'Back in Stock Notification'
              })
            );
          }

          const responses = await Promise.all(requests);
          if (responses[0].status === 202) {
            this.finalMessage = this.$t('products.waitlist.success_description');
            this.subscribed = true;
            this.subscribedVariantIds.push(variantId);
            trackGetNotified(productSKU); // TODO: Remove old tracking
            this.handleTracking();
          }
        } catch (error) {
          console.log(error)
        } finally {
          this.loading = false;
        }
      } else {
        console.warn('Issue absorbing product data');
      }
    },
  },
};
